<template>
	<div class="reattach-modal">
		<app-loader
			v-if="isListLoading || isSubmitting"
			:style="loaderStyle"
		/>

		<div class="reattach-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="reattach-modal__search-input"
				:placeholder="$t('client_search_placeholder')"
				show-search-button
			/>
			<div
				v-if="props.showCloseIcon"
				class="reattach-modal__close"
			>
				<app-icon
					name="close"
					width="20px"
					color="#D2D2D2"
					hover-color="#499259"
					cursor="pointer"
					:title="$t('close_label')"
					@click="emit('close')"
				/>
			</div>
		</div>

		<div class="reattach-modal__header">
			<div class="reattach-modal__header-title">
				{{ $t('clients_label') }}
			</div>
		</div>

		<div
			ref="$body"
			class="reattach-modal__body"
		>
			<div
				v-for="client in clients"
				:key="client.id"
				:class="{
					'reattach-modal__item': true,
					'reattach-modal__item--current': client.id === props.currentClientId
				}"
				@click="onSelect(client)"
			>
				<div class="reattach-modal__item-name">
					{{ client.name }}
					<span
						v-if="client.id === props.currentClientId"
						class="reattach-modal__item-badge"
					>{{ $t('current_lower') }}</span>
				</div>
				<div
					v-if="client.addressChain?.length"
					class="reattach-modal__item-address"
				>
					{{ client.addressChain.join(', ') }}
				</div>
			</div>

			<div
				v-if="!isListLoading && !clients.length"
				class="reattach-modal__empty"
			>
				{{ $t('clients_not_found') }}
			</div>

			<div
				v-if="errorText"
				class="reattach-modal__error"
			>
				{{ errorText }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import { i18n } from "@/i18n";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { financesStore } from "@/store/finances";
import { ReattachClient } from "@/models/interfaces/finances/counterparty-models";
import { debounce } from "lodash";

const props = defineProps<{
	counterpartyId: number,
	currentClientId?: number,
	showCloseIcon?: boolean,
}>()

const emit = defineEmits<{
	(e: 'reattached'): void,
	(e: 'close'): void,
}>()

const t = (key: string): string => String(i18n.global.t(key))

const finances = financesStore()

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const filter = ref('')
const clients = ref<ReattachClient[]>([])
const isListLoading = ref(false)
const isSubmitting = ref(false)
const errorText = ref('')

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const onFetch = async () => {
	try {
		isListLoading.value = true
		clients.value = await finances.getReattachClients(filter.value)
	} catch (e) {
		console.error(e)
	} finally {
		isListLoading.value = false
	}
}

const onSearch = async () => {
	$body.value?.scrollTo({ top: 0 })
	await onFetch()
}

const debouncedSearch = debounce(onSearch, 500)

const onSelect = async (client: ReattachClient) => {
	if (client.id === props.currentClientId || isSubmitting.value) {
		return
	}

	try {
		errorText.value = ''
		isSubmitting.value = true
		const ok = await finances.reattachCounterparty({
			counterpartyId: props.counterpartyId,
			clientId: client.id,
		})
		if (ok) {
			emit('reattached')
		} else {
			isSubmitting.value = false
			errorText.value = t('counterparty_reattach_error')
		}
	} catch (e) {
		isSubmitting.value = false
		errorText.value = t('counterparty_reattach_error')
		console.error(e)
	}
}

watch(filter, (newVal, oldVal) => {
	if (newVal !== oldVal) {
		debouncedSearch()
	}
})

onMounted(async () => {
	try {
		await onFetch()
		await nextTick(() => $input.value?.focusInput())
	} catch (e) {
		console.error(e)
	}
})
</script>

<style scoped lang="scss">
.reattach-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;
	}

	&__item {
		display: flex;
		flex-direction: column;
		gap: 2px;
		cursor: pointer;
		transition: color 0.1s;

		&-name {
			font-size: 14px;
			line-height: 18px;
			color: #333;
		}

		&-address {
			font-size: 12px;
			line-height: 16px;
			color: #979797;
		}

		&-badge {
			margin-left: 4px;
			font-size: 12px;
			line-height: 16px;
			color: #979797;
		}

		&:hover:not(&--current) &-name {
			text-decoration: underline;
			color: #396742;
		}

		&--current {
			cursor: default;

			.reattach-modal__item-name {
				color: #979797;
			}
		}
	}

	&__empty {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
	}

	&__error {
		font-size: 13px;
		line-height: 18px;
		color: #BA4C4C;
	}
}
</style>
